import type { ReactNode, JSX } from 'react'

import type { TooltipContentProps } from 'recharts'

import { InfoIcon, Text } from '@buffer-mono/popcorn'

import { ChartColorDot } from '../ChartColorDot'
import { getTooltipPoint } from './getTooltipPoint'
import { resolveValueAtTimestamp } from './resolveValueAtTimestamp'
import { MetricUnit, SummaryMode } from './types'
import type { MetricSeries } from './types'

import styles from './ChartTooltip.module.css'

function formatTooltipDate(timestamp: number, monthly = false): string {
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    ...(monthly ? {} : { day: 'numeric' }),
    year: 'numeric',
    timeZone: 'UTC',
  })
}

type ChartTooltipProps = Pick<
  Partial<TooltipContentProps<number, string>>,
  'active' | 'label' | 'payload'
> & {
  metrics: MetricSeries[]
  showIcons?: boolean
  /** Format the tooltip date as month-only (no day) for monthly-bucketed data. */
  monthly?: boolean
  /**
   * Overrides the primary date header formatting. Used by the bar charts to
   * render a bucket's period (e.g. a week range "Jun 8 – Jun 14, 2026") rather
   * than the single bucket-start day. Falls back to the default day/month
   * formatting when omitted.
   */
  formatDate?: (timestampMs: number) => string
}

interface TooltipEntry {
  key: string
  /** Clean metric title without the "(comparison)" suffix. */
  title: string
  color: string
  icon?: ReactNode
  value: number
  /** Unit suffix carried from the source metric; appended after the value. */
  unit?: MetricUnit
  originalDate?: string
  /** Present only for a carried-forward value; rendered as an "As of <date>" note. */
  asOfDate?: string
}

/** A visible channel whose value at the hovered day is unavailable — kept in the
 * list (rather than dropped) so the channel set stays stable across the range. */
interface NoDataEntry {
  key: string
  title: string
  color: string
  icon?: ReactNode
}

function formatTooltipValue(value: number, unit?: MetricUnit): string {
  if (unit === MetricUnit.Percentage) return `${value.toFixed(2)}%`
  return value.toLocaleString()
}

export function ChartTooltip({
  active,
  label,
  metrics,
  showIcons = false,
  monthly = false,
  formatDate,
}: ChartTooltipProps): JSX.Element | null {
  if (!active || label == null) return null

  const timestampMs = Number(label)
  const primaryEntries: TooltipEntry[] = []
  const comparisonEntries: TooltipEntry[] = []
  const noDataEntries: NoDataEntry[] = []

  // Comparison overlays resolve at the exact tick only (no carry-forward) and
  // never get a "no data" row; primary series are classified by getTooltipPoint.
  for (const metric of metrics) {
    const dataKey = metric.key
    const isComparison = dataKey.endsWith('-comparison')

    if (isComparison) {
      const resolved = resolveValueAtTimestamp(metric, timestampMs, {
        exact: true,
      })
      if (!resolved) continue
      const primaryKey = dataKey.slice(0, -'-comparison'.length)
      const primaryMetric = metrics.find((m) => m.key === primaryKey)
      comparisonEntries.push({
        key: dataKey,
        title: primaryMetric?.title ?? metric.title,
        color: metric.color,
        icon: showIcons ? (primaryMetric?.icon ?? metric.icon) : undefined,
        value: resolved.value,
        unit: primaryMetric?.unit ?? metric.unit,
        originalDate: resolved.originalDate,
      })
      continue
    }

    const point = getTooltipPoint(metric, timestampMs)
    if (point.kind === 'missing') {
      // A gap in a per-day Total (Posts) is just "no bar", not missing data.
      if (metric.summaryMode === SummaryMode.Total) continue
      noDataEntries.push({
        key: dataKey,
        title: metric.title,
        color: metric.color,
        icon: showIcons ? metric.icon : undefined,
      })
      continue
    }

    primaryEntries.push({
      key: dataKey,
      title: metric.title,
      color: metric.color,
      icon: showIcons ? metric.icon : undefined,
      value: point.value,
      unit: metric.unit,
      // Show the observed date for a carried-forward value so it reads as
      // "last known" rather than contradicting the interpolated line.
      asOfDate:
        point.kind === 'computed'
          ? formatTooltipDate(new Date(point.sampledDate).getTime(), monthly)
          : undefined,
    })
  }

  const primaryDate = formatDate
    ? formatDate(timestampMs)
    : formatTooltipDate(timestampMs, monthly)
  const comparisonDate = comparisonEntries.find(
    (e) => e.originalDate,
  )?.originalDate
  const comparisonDateLabel = comparisonDate
    ? formatTooltipDate(new Date(comparisonDate).getTime(), monthly)
    : ''

  if (
    primaryEntries.length === 0 &&
    comparisonEntries.length === 0 &&
    noDataEntries.length === 0
  ) {
    return null
  }

  return (
    <div className={styles.tooltip}>
      {(primaryEntries.length > 0 || noDataEntries.length > 0) && (
        <div className={styles.tooltipGroup}>
          <Text size="sm" color="subtle">
            {primaryDate}
          </Text>
          {primaryEntries.map((entry) => (
            <div key={entry.key} className={styles.tooltipEntry}>
              <div className={styles.tooltipItem}>
                <ChartColorDot color={entry.color} />
                {entry.icon}
                <Text size="sm" className={styles.tooltipItemLabel}>
                  {entry.title}
                </Text>
                <Text size="sm" weight="bold" className={styles.tooltipValue}>
                  {formatTooltipValue(entry.value, entry.unit)}
                </Text>
              </div>
              {entry.asOfDate != null && (
                <div className={styles.note}>
                  <InfoIcon size="xsmall" color="subtle" />
                  <Text size="sm" className={styles.noteText}>
                    {`As of ${entry.asOfDate}`}
                  </Text>
                </div>
              )}
            </div>
          ))}
          {noDataEntries.map((entry) => (
            <div key={entry.key} className={styles.tooltipEntry}>
              <div className={styles.tooltipItem}>
                <ChartColorDot color={entry.color} />
                {entry.icon}
                <Text size="sm" className={styles.tooltipItemLabel}>
                  {entry.title}
                </Text>
                <Text size="sm" color="subtle" className={styles.tooltipValue}>
                  -
                </Text>
              </div>
              <div className={styles.note}>
                <InfoIcon size="xsmall" color="subtle" />
                <Text size="sm" className={styles.noteText}>
                  Data unavailable
                </Text>
              </div>
            </div>
          ))}
        </div>
      )}
      {comparisonEntries.length > 0 && (
        <div className={styles.tooltipGroup}>
          <Text size="sm" color="subtle">
            {comparisonDateLabel}
          </Text>
          {comparisonEntries.map((entry) => (
            <div key={entry.key} className={styles.tooltipItem}>
              <ChartColorDot color={entry.color} dashed />
              {entry.icon}
              <Text size="sm" className={styles.tooltipItemLabel}>
                {entry.title}
              </Text>
              <Text size="sm" weight="bold" className={styles.tooltipValue}>
                {formatTooltipValue(entry.value, entry.unit)}
              </Text>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
